import { useAdminApi } from '@hooks/useAdminApi';
import { Box, Stack } from '@mui/material';
import { Organization } from '@protos/organization';
import { User } from '@protos/user';
import { TextInputSelectBox } from '@shared/components/TextFieldSelectBox';
import { useEffect, useState } from 'react';
import { StyledIconButton } from './StyledIconButton';
import { BaseEditDialog } from './BaseEditDialog';
import { getCurrentUser } from '../utils';

interface EditTradeAccountsDialogProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: (user: User) => void;
  orgData: Organization;
}

export const EditTradeAccountsDialog: React.FC<EditTradeAccountsDialogProps> = ({ user, isOpen, onClose, onUserUpdated, orgData }) => {
  const { apiClient } = useAdminApi();
  const [tradeAccounts, setTradeAccounts] = useState<string[]>([...(user.trading_accounts ?? [])]);
  const currentUser = getCurrentUser(user);

  const onSave = async () => {
    if (apiClient && user) {
      await apiClient.updateUserLimits(user.id, { ...currentUser, trading_accounts: tradeAccounts }).then(data => {
        onUserUpdated(data);
      });
    }
  };

  const onTradeAccountAdd = () => {
    setTradeAccounts([...tradeAccounts, '']);
  };

  const onTradeAccountRemove = (tradeAccountId: number) => {
    const updatedTradeAccounts = [...tradeAccounts];
    updatedTradeAccounts.splice(tradeAccountId, 1);
    setTradeAccounts(updatedTradeAccounts);
  };

  const onTradeAccountUpdate = (tradeAccountId: number, updatedValue: string) => {
    const updatedTradeAccounts = [...tradeAccounts];
    updatedTradeAccounts.splice(tradeAccountId, 1, updatedValue);
    setTradeAccounts(updatedTradeAccounts);
  };

  useEffect(() => {
    setTradeAccounts(user.trading_accounts ?? []);
  }, [user, isOpen]);

  return (
    <BaseEditDialog
      title="Edit Trade Accounts"
      isOpen={isOpen}
      onClose={onClose}
      clearErrorOn={[tradeAccounts]}
      defaultErrorMessage="There was an error updating the trade accounts"
      onSave={onSave}
    >
      <Box display="flex" marginRight={2} gap={1}>
        <Stack direction="column" alignItems="flex-start">
          {tradeAccounts.map((tradeAccount, id) => (
            <Stack key={`trade_account_${tradeAccount}`} direction="row">
              <TextInputSelectBox
                label="Trade Account"
                value={tradeAccount}
                sx={{ width: '100px' }}
                onChange={(value: string) => onTradeAccountUpdate(id, value)}
                options={orgData.trading_accounts.filter(p => !tradeAccounts.includes(p) || p === tradeAccount)}
              />
              <StyledIconButton onClick={() => onTradeAccountRemove(id)} size={'small'} style={{ marginLeft: '5px' }}>
                <i className={'ri-delete-bin-line'} />
              </StyledIconButton>
            </Stack>
          ))}
          <StyledIconButton onClick={onTradeAccountAdd}>
            <i className={'ri-add-line'} />
          </StyledIconButton>
        </Stack>
      </Box>
    </BaseEditDialog>
  );
};
